<script setup>
import {
  computed,
  inject,
  watch,
  ref,
  onMounted,
  onUnmounted,
  nextTick,
  toRefs,
} from "vue";
import BaseCom from "./BaseCom.vue";
import VideoPlayer from "./VideoPlayer.vue";
// 定义组件属性
const props = defineProps({
  // 新增背景相关属性 - 只传入type和src，其他属性固定写死
  contentId: {
    type: String,
    default: "",
  },
  // 容器尺寸属性
  containerStyle: {
    type: Object,
    default: () => ({
      width: "100%",
      height: "100%",
    }),
  },
  // 是否允许全屏播放
  allowFullScreenPlay: {
    type: Boolean,
    default: true,
  },
  // 懒加载阈值，默认200px，即距离视口200px时开始加载
  lazyThreshold: {
    type: Number,
    default: 200,
  },
  // 是否显示加载中遮罩层
  showLoadingOverlay: {
    type: Boolean,
    default: true,
  },
  // 是否自动播放视频
  autoPlay: {
    type: Boolean,
    default: true,
  },
  // 背景图片大小，默认contain
  backgroundSize: {
    type: String,
    default: "contain",
  },
  // 背景图片位置，默认center
  backgroundPosition: {
    type: String,
    default: "center",
  },
  // 背景图片重复，默认no-repeat
  backgroundRepeat: {
    type: String,
    default: "no-repeat",
  },
});
const { contentId } = toRefs(props);
// 是否正在加载
const isLoading = ref(true);
const defaultVal = {
  type: "image", // 'image' 或 'video'
  src: "", // 背景图片或视频的URL
};
// 定义emits
const emit = defineEmits(["edit"]);
const background = ref({
  type: "image", // 'image' 或 'video'
  src: "", // 背景图片或视频的URL
});
// 是否正在全屏播放
const isFullScreenPlaying = ref(false);
// 视频元素引用
const videoRef = ref(null);
// 容器引用，用于Intersection Observer
const containerRef = ref(null);
// 用于Intersection Observer观察的元素引用
const observeRef = ref(null);
// 是否进入视口，用于懒加载
const isInViewport = ref(false);
// Intersection Observer实例
let observer = null;

// 处理编辑事件
const handleEdit = () => {
  openBackgroundEditor(props.contentId);
};
const contentMap = inject("contentMap", defaultVal);
const isLoggedIn = inject("isLoggedIn", false);
const openBackgroundEditor = inject("openBackgroundEditor", null);

// 监听contentId变化
watch(
  contentMap,
  (newValue) => {
    background.value = newValue[props.contentId] || defaultVal;
    // 初始化加载状态
    isLoading.value = true;

    // 如果没有src或类型不支持，直接设置为加载完成
    if (
      !background.value.src ||
      !["image", "video"].includes(background.value.type)
    ) {
      isLoading.value = false;
      return;
    }

    // 如果是图片类型，使用Image对象预加载
    if (background.value.type === "image" && isInViewport.value) {
      const img = new Image();
      img.onload = () => {
        isLoading.value = false;
      };
      img.onerror = () => {
        isLoading.value = false;
      };
      img.src = background.value.src;
    }
    // 如果是视频类型，不需要预加载，视频元素会处理
    else if (background.value.type === "video") {
      // 视频元素会通过自身事件处理加载状态
    }
    // 如果不在视口内，直接设置为加载完成
    else if (!isInViewport.value) {
      isLoading.value = false;
    }
  },
  { immediate: true }
);

// 监听isInViewport变化
watch(isInViewport, (newVal) => {
  // 如果进入视口且是图片类型，需要重新加载
  if (newVal && background.value.type === "image" && background.value.src) {
    isLoading.value = true;
    const img = new Image();
    img.onload = () => {
      isLoading.value = false;
    };
    img.onerror = () => {
      isLoading.value = false;
    };
    img.src = background.value.src;
  }
  // 如果不在视口内，直接设置为加载完成
  else if (!newVal) {
    isLoading.value = false;
  }
});
watch(contentId, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    background.value = contentMap.value[newVal] || defaultVal;
    // 初始化加载状态
    isLoading.value = true;
    // 如果没有src或类型不支持，直接设置为加载完成
    if (
      !background.value.src ||
      !["image", "video"].includes(background.value.type)
    ) {
      isLoading.value = false;
      return;
    }

    // 如果是图片类型，使用Image对象预加载
    if (background.value.type === "image" && isInViewport.value) {
      const img = new Image();
      img.onload = () => {
        isLoading.value = false;
      };
      img.onerror = () => {
        isLoading.value = false;
      };
      img.src = background.value.src;
    }
    // 如果是视频类型，不需要预加载，视频元素会处理
    else if (background.value.type === "video") {
      // 视频元素会通过自身事件处理加载状态
    }
    // 如果不在视口内，直接设置为加载完成
    else if (!isInViewport.value) {
      isLoading.value = false;
    }
  }
});

// 处理元素进入视口
const handleIntersection = (entries) => {
  const entry = entries[0];
  if (entry.isIntersecting) {
    isInViewport.value = true;
    // 停止观察
    if (observer) {
      if (observeRef.value) {
        observer.unobserve(observeRef.value);
      } else if (containerRef.value) {
        observer.unobserve(containerRef.value);
      }
    }
  }
};

// 初始化Intersection Observer
const initObserver = () => {
  // 如果已经在视口内，直接加载
  if (isInViewport.value) return;

  if ("IntersectionObserver" in window) {
    // 清理之前的observer
    if (observer) {
      observer.disconnect();
    }

    observer = new IntersectionObserver(handleIntersection, {
      root: null, // 使用视口作为根元素
      rootMargin: `${props.lazyThreshold}px ${props.lazyThreshold}px ${props.lazyThreshold}px ${props.lazyThreshold}px`, // 提前200px开始加载
      threshold: 0.01, // 1%的元素进入视口就触发
    });

    // 尝试观察observeRef或containerRef
    const targetElement = observeRef.value || containerRef.value;
    if (targetElement) {
      observer.observe(targetElement);
    }
  } else {
    // 不支持Intersection Observer的浏览器，直接加载
    isInViewport.value = true;
  }
};

// 计算背景样式 - 其他属性固定写死
const backgroundStyle = computed(() => {
  const style = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    opacity: 1, // 固定透明度为1
    zIndex: 0,
  };

  // 图片背景样式 - 使用props传入的背景属性
  if (
    background.value.type === "image" &&
    background.value.src
  ) {
    style.backgroundImage = `url(${background.value.src})`;
    style.backgroundSize = props.backgroundSize; // 使用props传入的值
    style.backgroundPosition = props.backgroundPosition; // 使用props传入的值
    style.backgroundRepeat = props.backgroundRepeat; // 使用props传入的值
  }

  // 全屏播放时的样式
  if (isFullScreenPlaying.value) {
    style.position = "fixed";
    style.zIndex = 9999;
  }

  return style;
});

// 计算容器样式
const computedContainerStyle = computed(() => {
  return {
    ...props.containerStyle,
    position: "relative",
    // 移除overflow: hidden，允许子元素使用sticky定位
  };
});

// 处理全屏播放
const handleFullScreenPlay = () => {
  if (!background.value.src || background.value.type !== "video") return;

  isFullScreenPlaying.value = true;

  // 暂停原始视频
  if (videoRef.value) {
    videoRef.value.pause();
  }
};

// 关闭全屏播放
const handleCloseFullScreen = () => {
  isFullScreenPlaying.value = false;

  // 恢复原始视频的播放
  if (videoRef.value) {
    videoRef.value.play();
  }
};

// 生命周期钩子
onMounted(() => {
  // 初始化Intersection Observer
  nextTick(() => {
    initObserver();
  });
});

onUnmounted(() => {
  // 清理Intersection Observer
  if (observer) {
    if (observeRef.value) {
      observer.unobserve(observeRef.value);
    } else if (containerRef.value) {
      observer.unobserve(containerRef.value);
    }
    observer.disconnect();
  }
});
</script>

<template>
  <!-- 使用BaseCom组件 -->
  <BaseCom
    ref="containerRef"
    :editable="isLoggedIn"
    @edit="handleEdit"
    :style="computedContainerStyle"
    :contentId="props.contentId"
  >
    <!-- 用于Intersection Observer观察的元素 -->
    <div
      ref="observeRef"
      class="observe-element"
      style="
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: -1;
      "
    ></div>

    <!-- 加载中遮罩层 -->
    <!-- <div v-if="isLoading && showLoadingOverlay" class="loading-overlay"></div> -->

    <!-- 背景层 -->
    <div
      v-if="background.type === 'image' && background.src"
      :style="backgroundStyle"
      class="background-img"
    ></div>

    <!-- 视频背景 - 属性固定写死 -->
    <video
      ref="videoRef"
      v-else-if="background.type === 'video' && background.src"
      :style="backgroundStyle"
      :src="background.src"
      :autoplay="autoPlay"
      muted
      loop
      playsinline
      webkit-playsinline
      x5-playsinline
      preload="auto"
      @loadeddata="
        () => {
          isLoading = false;
        }
      "
      @loadedmetadata="isLoading = false"
      @error="isLoading = false"
    >
      您的浏览器不支持视频标签
    </video>

    <!-- 全屏播放按钮 - 仅在允许全屏播放、有视频且视频已加载时显示 -->
    <button
      v-if="
        background.fullscreenEnabled &&
        background.type === 'video' &&
        background.src
      "
      class="full-screen-play-btn"
      @click="handleFullScreenPlay"
    >
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="12"
          cy="12"
          r="11"
          stroke="white"
          stroke-width="2"
          stroke-opacity="0.8"
        />
        <polygon points="9,7 17,12 9,17" fill="white" />
      </svg>
    </button>

    <!-- 全屏VideoPlayer组件 -->
    <div
      v-if="
        isFullScreenPlaying && background.type === 'video' && background.src
      "
      class="full-screen-player-overlay"
      @click="handleCloseFullScreen"
    >
      <div class="full-screen-player-container" @click.stop>
        <VideoPlayer
          :src="background.src"
          :autoplay="autoPlay"
          :loop="true"
          :muted="false"
          width="100%"
          height="100%"
          @close="handleCloseFullScreen"
        />
      </div>
      <button class="close-full-screen-btn" @click="handleCloseFullScreen">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
          <path
            d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
          />
        </svg>
      </button>
    </div>

    <!-- 内容区域，确保在背景上方 -->
    <div style="position: relative; z-index: 1; width: 100%; height: 100%">
      <slot></slot>
    </div>
  </BaseCom>
</template>

<style scoped>
/* 确保BaseCom的内容区域样式正确 */
:deep(.content-area) {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 确保视频正确显示 */
video {
  object-fit: cover;
}
.background-img {
  /* 背景图片样式已在computed中通过:style动态绑定 */
}

/* 全屏播放按钮样式 */
.full-screen-play-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 5;
}

.full-screen-play-btn:hover {
  background: rgba(0, 0, 0, 0.7);
  transform: translate(-50%, -50%) scale(1.1);
}

.full-screen-play-btn:active {
  transform: translate(-50%, -50%) scale(0.95);
}

/* 全屏播放器样式 */
.full-screen-player-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1005;
  cursor: pointer;
}

.full-screen-player-container {
  width: 100%;
  height: 100%;
  max-width: 1400px;
  max-height: 800px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
}

.close-full-screen-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10000;
}

.close-full-screen-btn:hover {
  background: rgba(0, 0, 0, 0.8);
  transform: scale(1.1);
}

.close-full-screen-btn:active {
  transform: scale(0.95);
}

/* 加载中遮罩层样式 */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 3;
  animation: breathe 2s ease-in-out infinite;
}
:deep(.mask-overlay) {
  padding: 8px;
}

/* 呼吸灯动画效果 */
@keyframes breathe {
  0% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    opacity: 0.4;
  }
}

/* 响应式调整 */
@media (max-width: 768px) {
  .full-screen-player-container {
    max-width: 100%;
    max-height: 100%;
  }

  .close-full-screen-btn {
    top: 10px;
    right: 10px;
    width: 36px;
    height: 36px;
  }
}
</style>
